import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiAlertTriangle, FiCheckCircle, FiLoader, FiTerminal, FiDatabase } from 'react-icons/fi';

export default function StateSimulator() {
  const [state, setState] = useState('success'); // success, error, loading, empty

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1 space-y-2">
        {['success', 'loading', 'error', 'empty'].map(s => (
          <button
            key={s}
            onClick={() => setState(s)}
            className={`w-full text-left px-4 py-3 rounded-xl border font-mono text-xs uppercase transition-all flex items-center justify-between ${
              state === s 
                ? 'bg-navy-800 border-quest-500 text-quest-500 shadow-glow-green' 
                : 'bg-navy-900 border-navy-700 text-gray-500 hover:text-white'
            }`}
          >
            {s}
            <div className={`w-2 h-2 rounded-full ${
              s === 'success' ? 'bg-quest-500' :
              s === 'error' ? 'bg-neon-pink' :
              s === 'loading' ? 'bg-neon-blue animate-pulse' : 'bg-gray-700'
            }`} />
          </button>
        ))}
      </div>

      <div className="lg:col-span-3 min-h-[300px] bg-navy-950 border border-navy-800 rounded-2xl flex items-center justify-center p-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 p-3 flex gap-2">
           <div className="w-2 h-2 rounded-full bg-red-500/50" />
           <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
           <div className="w-2 h-2 rounded-full bg-green-500/50" />
        </div>
        
        <AnimatePresence mode="wait">
          {state === 'success' && (
            <motion.div 
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-2xl bg-quest-500/10 border border-quest-500 flex items-center justify-center mx-auto mb-6 shadow-glow-green">
                <SafeIcon icon={FiCheckCircle} className="text-4xl text-quest-500" />
              </div>
              <h4 className="text-xl font-bold mb-2">Operation Complete</h4>
              <p className="text-gray-400 font-mono text-sm max-w-xs mx-auto">New skill node successfully synthesized into neural profile.</p>
            </motion.div>
          )}

          {state === 'loading' && (
            <motion.div 
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
            >
              <SafeIcon icon={FiLoader} className="text-5xl text-neon-blue animate-spin mx-auto mb-6" />
              <h4 className="text-xl font-bold mb-2">Processing...</h4>
              <p className="text-gray-400 font-mono text-sm">Compiling logic gates and validating XP distribution.</p>
            </motion.div>
          )}

          {state === 'error' && (
            <motion.div 
              key="error"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-2xl bg-neon-pink/10 border border-neon-pink flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiAlertTriangle} className="text-4xl text-neon-pink" />
              </div>
              <h4 className="text-xl font-bold mb-2 text-neon-pink uppercase tracking-widest">Access Denied</h4>
              <p className="text-gray-400 font-mono text-sm max-w-xs mx-auto">Insufficient energy levels. Recharge protocol required (80/100).</p>
            </motion.div>
          )}

          {state === 'empty' && (
            <motion.div 
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-2xl bg-navy-800 border border-navy-700 flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiDatabase} className="text-4xl text-gray-600" />
              </div>
              <h4 className="text-xl font-bold mb-2 text-gray-500">No Data Found</h4>
              <p className="text-gray-400 font-mono text-sm">Initialize a Skill Path to begin populating this sector.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}